import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus, Trash2, KeyRound, ShieldCheck, Shield, X, UserPlus, ShieldAlert } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ResetMfaButton } from "@/components/admin/ResetMfaButton";

const EMPTY_NEW = { email: "", name: "", password: "", role: "admin" };

const Portal = ({ children }) => createPortal(children, document.body);

export default function AdminUsersPage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [users, setUsers] = useState([]);
    const [err, setErr] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [form, setForm] = useState(EMPTY_NEW);
    const [busy, setBusy] = useState(false);
    const [pwTarget, setPwTarget] = useState(null);  // user being password-reset
    const [newPw, setNewPw] = useState("");
    const [confirmDel, setConfirmDel] = useState(null);

    const refresh = async () => {
        setErr("");
        try {
            const r = await api.get("/admin/admins");
            setUsers(r.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { if (isSuper) refresh(); }, [isSuper]);

    if (!isSuper) {
        return (
            <div data-testid="admin-users-page" className="p-10 max-w-md mx-auto text-center">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <ShieldAlert className="w-10 h-10 mx-auto text-amber-600 mb-3" />
                    <h2 className="font-bold text-base mb-1">Super admin only</h2>
                    <p className="text-xs text-slate-600">Admin user management is restricted to super admins.</p>
                </div>
            </div>
        );
    }

    const create = async () => {
        setErr(""); setBusy(true);
        try {
            await api.post("/admin/admins", {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                password: form.password,
                role: form.role,
            });
            setShowNew(false); setForm(EMPTY_NEW);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const toggleRole = async (u) => {
        const nextRole = u.role === "super_admin" ? "admin" : "super_admin";
        setErr("");
        try {
            await api.patch(`/admin/admins/${u.user_id}`, { role: nextRole });
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const doResetPw = async () => {
        if (!pwTarget) return;
        setErr(""); setBusy(true);
        try {
            await api.post(`/admin/admins/${pwTarget.user_id}/reset-password`, { new_password: newPw });
            setPwTarget(null); setNewPw("");
            // No refresh needed — nothing visible changed.
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const doDelete = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/admin/admins/${confirmDel.user_id}`);
            setConfirmDel(null);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); setConfirmDel(null); }
    };

    return (
        <div data-testid="admin-users-page" className="space-y-5 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <ShieldCheck className="w-6 h-6 text-blue-600" /> Admin Users
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">Super admins manage vendor credentials + admin accounts. Regular admins run day-to-day ops.</p>
                </div>
                <button onClick={() => { setForm(EMPTY_NEW); setShowNew(true); }}
                        data-testid="admin-user-new"
                        className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                    <UserPlus className="w-3.5 h-3.5" /> Add Admin
                </button>
            </div>

            {err && <div data-testid="admin-users-error" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                        <tr>
                            <th className="px-4 py-2.5 text-left">Email</th>
                            <th className="px-4 py-2.5 text-left">Name</th>
                            <th className="px-4 py-2.5 text-left">Role</th>
                            <th className="px-4 py-2.5 text-left">Added</th>
                            <th className="px-4 py-2.5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No admin users.</td></tr>}
                        {users.map((u) => {
                            const isSelf = u.user_id === user?.user_id;
                            return (
                                <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-slate-100">
                                    <td className="px-4 py-2.5 font-mono text-xs">{u.email}{isSelf && <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[9px] font-semibold uppercase">You</span>}</td>
                                    <td className="px-4 py-2.5">{u.name}</td>
                                    <td className="px-4 py-2.5">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                            u.role === "super_admin" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-700"
                                        }`} data-testid={`admin-role-${u.user_id}`}>
                                            {u.role === "super_admin" ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                                            {u.role === "super_admin" ? "Super Admin" : "Admin"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-slate-500">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-right space-x-3 text-xs">
                                        <ResetMfaButton userType="admin" user={u} disabled={isSelf} onDone={refresh} />
                                        <button onClick={() => toggleRole(u)}
                                                disabled={isSelf}
                                                data-testid={`admin-toggle-role-${u.user_id}`}
                                                className="text-blue-600 hover:text-blue-800 underline disabled:opacity-30 disabled:no-underline">
                                            {u.role === "super_admin" ? "Demote" : "Promote"}
                                        </button>
                                        <button onClick={() => { setPwTarget(u); setNewPw(""); }}
                                                data-testid={`admin-reset-pw-${u.user_id}`}
                                                className="text-amber-600 hover:text-amber-800 underline inline-flex items-center gap-1">
                                            <KeyRound className="w-3 h-3" /> Reset PW
                                        </button>
                                        <button onClick={() => setConfirmDel(u)}
                                                disabled={isSelf}
                                                data-testid={`admin-delete-${u.user_id}`}
                                                className="text-rose-600 hover:text-rose-800 underline disabled:opacity-30 disabled:no-underline inline-flex items-center gap-1">
                                            <Trash2 className="w-3 h-3" /> Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Create modal */}
            {showNew && (<Portal>
                <div data-testid="admin-user-new-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[460px] shadow-xl">
                        <div className="flex items-start justify-between mb-4">
                            <h3 className="font-bold text-lg">Add Admin</h3>
                            <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Email</label>
                                <input data-testid="admin-user-email" type="email" value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="ops@yourco.com" className="input w-full text-sm mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Name</label>
                                <input data-testid="admin-user-name" value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Jane Doe" className="input w-full text-sm mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                                    Password <span className="text-slate-400 normal-case">(≥10 chars, mixed case + digit + symbol)</span>
                                </label>
                                <input data-testid="admin-user-password" type="password" value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        className="input w-full text-sm mt-1 font-mono" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Role</label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    {[
                                        ["admin", "Admin", "Day-to-day ops"],
                                        ["super_admin", "Super Admin", "Full vendor + admin control"],
                                    ].map(([val, lbl, sub]) => (
                                        <button key={val} type="button"
                                                data-testid={`admin-user-role-${val}`}
                                                onClick={() => setForm({ ...form, role: val })}
                                                className={`p-2.5 rounded-md text-xs border transition ${
                                                    form.role === val
                                                        ? "border-blue-500 bg-blue-50 text-blue-800 font-semibold"
                                                        : "border-slate-200 hover:border-slate-300"
                                                }`}>
                                            <div className="font-semibold">{lbl}</div>
                                            <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setShowNew(false)} className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={create} disabled={busy}
                                    data-testid="admin-user-create"
                                    className="btn-primary px-4 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Create
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}

            {/* Password reset modal */}
            {pwTarget && (<Portal>
                <div data-testid="admin-user-pw-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[440px] shadow-xl">
                        <div className="flex items-start justify-between mb-3">
                            <h3 className="font-bold text-lg">Reset password</h3>
                            <button onClick={() => setPwTarget(null)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-600 mb-3">
                            Reset password for <span className="font-mono font-semibold">{pwTarget.email}</span>. Their active sessions will be terminated immediately.
                        </p>
                        <input data-testid="admin-user-newpw" type="password" value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="New password" className="input w-full text-sm mt-1 font-mono" />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setPwTarget(null)} className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={doResetPw} disabled={busy || newPw.length < 10}
                                    data-testid="admin-user-pw-submit"
                                    className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Reset password
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}

            {/* Delete confirm */}
            {confirmDel && (<Portal>
                <div data-testid="admin-user-del-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[420px] shadow-xl">
                        <h3 className="font-bold text-base mb-1">Delete admin?</h3>
                        <p className="text-xs text-slate-600 mb-4">
                            <span className="font-mono font-semibold">{confirmDel.email}</span> will lose access immediately. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirmDel(null)} className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={doDelete} data-testid="admin-user-del-confirm"
                                    className="px-3 py-2 rounded-md text-xs bg-rose-600 text-white hover:bg-rose-700 font-semibold">Delete</button>
                        </div>
                    </div>
                </div>
            </Portal>)}
        </div>
    );
}
